import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  BsDatepickerDirective,
  BsDatepickerContainerComponent,
  ɵk as BsDatepickerNavigationViewComponent,
  ɵl as BsDaysCalendarViewComponent, DatepickerDateCustomClasses, BsLocaleService,
} from 'ngx-bootstrap/datepicker';
import moment from 'moment';
import { IDatePeriod, IDatepickerConfig, ITabValue } from '../types';
import { CellHoverEvent, DayViewModel } from 'ngx-bootstrap/datepicker/models';
import { DateGranularity } from '../datepicker-specific-constants';
import { detectMobile } from '@xenial-ux/utils';
import * as locales from 'ngx-bootstrap/locale';
import { defineLocale, LocaleData, updateLocale } from 'ngx-bootstrap/chronos';

@Component({
  selector: 'xux-datepicker-single',
  templateUrl: './xux-datepicker-single.component.html',
  styleUrls: ['./xux-datepicker-single.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    BsDatepickerDirective,
    BsDaysCalendarViewComponent,
    BsDatepickerContainerComponent,
    BsDatepickerNavigationViewComponent,
  ],
})
export class XuxDatepickerSingleComponent implements OnInit, AfterViewInit {
  @Input() set calendarValue(value: IDatePeriod) {
    let defaultValue = value.dateStart instanceof Date ? value.dateStart : value.dateStart
        && moment(value.dateStart).toDate();
    if (defaultValue && this.config && this.config.minDate) {
      defaultValue = (defaultValue < this.config.minDate) ? this.config.minDate : defaultValue;
    }
    if (defaultValue && this.config && this.config.maxDate) {
      defaultValue = (defaultValue > this.config.maxDate) ? this.config.maxDate : defaultValue;
    }
    this._bsValue = defaultValue;
    if (this.initialized) {
      this.setValue(this._bsValue);
    }
  }
  @Input() public config: IDatepickerConfig;

  public dpConfig: any = {
    containerClass: 'theme-dark-blue',
    showWeekNumbers: false,
    displayMonths: 1,
    selectFromOtherMonth: true,
  };
  protected value: Date;
  protected tabValue: ITabValue = {};
  public _bsValue: Date;
  public showDropdown: { year: boolean, month: boolean } = {
    year: false,
    month: false,
  };
  public DateGranularity: typeof  DateGranularity = DateGranularity;
  public months: { number: number, name: string }[];
  public years: number[] = [];
  public dayTooltipTitle: string = '';
  public customClasses: DatepickerDateCustomClasses[];
  @Output() public onChange: EventEmitter<IDatePeriod> = new EventEmitter();
  @ViewChild('monthSelector', { static: false }) private monthSelector: ElementRef;
  @ViewChild('yearsSelector', { static: false }) private yearsSelector: ElementRef;
  @ViewChild('dayTooltipContainer', { static: false }) private dayTooltipContainer: ElementRef;
  private initialized: boolean = false;
  private internalDpMethods: { monthSelectHandler: Function, yearSelectHandler: Function } = {
    monthSelectHandler: null,
    yearSelectHandler: null,
  };

  constructor(
    private bsLocaleService: BsLocaleService,
    private datepickerDirective: BsDatepickerDirective,
    private bsDatepickerContainerComponent: BsDatepickerContainerComponent,
    private bsDatepickerNavigationViewComponent: BsDatepickerNavigationViewComponent,
    private elementRef: ElementRef
  ) {
    this.months = moment.monthsShort()
      .map((monthName, index) => ({ number: index, name: monthName }));
  }

  public ngOnInit(): void {
    const minYear = this.config.yearsRange[0];
    const maxYear = this.config.yearsRange[1];
    for (let i = maxYear; i >= minYear; i -= 1) {
      this.years.push(i);
    }

    this.initialized = true;
    if (this._bsValue) {
      this.setValue(this._bsValue);
    }

    this.replaceDpMethods();
    if (this.config.locale) {
      this.config.locale = this.config.locale.split('-')[0];
      this.setLocale(this.config.locale);
      this.dpConfig.locale = this.config.locale;
    }
  }

  public ngAfterViewInit(): void {
    this.insertCustomDropdowns();
  }

  public showMonth(month: number, isOtherMonth: boolean = false): void {
    if (!isOtherMonth && this.isDateDisabled(DateGranularity.MONTH, month)) { return; }

    const currentYear = moment(this.tabValue.viewDate).get(DateGranularity.YEAR);
    const monthDate = moment.isDate(month) ? month : moment([currentYear, month]).toDate();
    const event = { date: monthDate, label: '' };
    this.tabValue.viewDate = monthDate;
    this.internalDpMethods.monthSelectHandler(event);
    this.showDropdown.month = false;
    this.showDropdown.year = false;
    this.insertCustomDropdowns();
  }

  public showYear(year: number, month?: number): void {
    if (this.isDateDisabled(DateGranularity.YEAR, year)) { return; }

    const newDate = moment(this.tabValue.viewDate).set('year', year).toDate();
    const event = { date: newDate, label: '' };
    this.tabValue.viewDate = newDate;
    this.internalDpMethods.yearSelectHandler(event);

    let currentMonth = this.tabValue.viewDate.getMonth();
    if (month !== undefined) {
      currentMonth = month;
    }

    this.showMonth(currentMonth);
    this.insertCustomDropdowns();
  }

  public isDateDisabled(unit: DateGranularity.MONTH | DateGranularity.YEAR, value: number): boolean {
    const newValue = moment(this.tabValue.viewDate).set(unit, value);

    const isBeforeMin = !!this.config.minDate && newValue.isBefore(this.config.minDate, unit);
    const isAfterMax = !!this.config.maxDate && newValue.isAfter(this.config.maxDate, unit);
    return isBeforeMin || isAfterMax;
  }

  public onValueChange(newValue: Date): void {
    this.setValue(newValue);
    this.onChange.next({ dateStart: this.value });
  }

  private insertCustomDropdowns(): void {
    this.showDropdown.month = false;
    this.showDropdown.year = false;

    setTimeout(() => {
      const monthsContainer = this.elementRef.nativeElement
          .querySelector('bs-datepicker-navigation-view .current');
      const yearsContainer = this.elementRef.nativeElement
          .querySelector('bs-datepicker-navigation-view .current + .current');
      if (monthsContainer) {
        monthsContainer
            .appendChild(this.monthSelector.nativeElement);
      }
      if (yearsContainer) {
        yearsContainer
            .appendChild(this.yearsSelector.nativeElement);
      }
    }, 10);
  }

  private setValue(newValue: Date): void {
    this.value = newValue;
    const format = this.config.dateFormat.long_date_format;
    if (this.value) {
      this.customClasses = [{ date: this.value, classes: ['in-selected-range'] }];
      this.tabValue.date = moment(this.value).format(format);
      this.tabValue.viewDate = this.value;
    }

    this.insertCustomDropdowns();
  }

  private isEditable(): boolean {
    return !!this.config.availableOptions
      .filter(option => option.id === 'single')
      .length;
  }

  private replaceDpMethods(): void {
    const self = this;

    // workaround until datepicker has inline mode.
    // .hide() method is rewritten to keep calendar opened & working when dates are selected.
    const replaceHideMethod = (): void => {
      (<any>this.datepickerDirective).__proto__.hide = (): void => {};
    };

    const replaceViewMethod = (): void => {
      type YearOrMonth = DateGranularity.YEAR | DateGranularity.MONTH;
      (<any>this.bsDatepickerNavigationViewComponent).__proto__.view = (name: YearOrMonth): void => {
        if (!this.isEditable()) {
          return;
        }

        if (name === DateGranularity.YEAR) {
          this.showDropdown[DateGranularity.MONTH] = false;
        }

        if (name === DateGranularity.MONTH) {
          this.showDropdown[DateGranularity.YEAR] = false;
        }

        this.showDropdown[name] = !this.showDropdown[name];
      };
    };

    const replaceDaySelectHandler = (): Function => {
      return function (this: any, day: DayViewModel): void {
        let currentMonth = day.date.getMonth();
        let currentYear = day.date.getFullYear();
        if (day.isOtherMonth) {
          if (Number(day.label) <= 15) {
            currentMonth -= 1;
            if (currentMonth < 0) {
              currentMonth = 11;
              currentYear -= 1;
            }
          } else {
            currentMonth += 1;
            if (currentMonth > 11) {
              currentMonth = 0;
              currentYear += 1;
            }
          }
        }
        if (day.isDisabled) {
          return;
        }

        self.customClasses = [];
        self.setValue(day.date);
        this.__proto__.daySelectHandler.call(this, day);
        self.showMonth(currentMonth, day.isOtherMonth);
        self.showYear(currentYear, currentMonth);
      };
    };

    // Since we are unable to get access to inner bootstrap components or directives
    // used decorator for dayHoverHandler method
    // in order to get access to day cell data needed for tooltip
    const replaceOnDayHoverMethod = (bsDatepickerContainerComponent: BsDatepickerContainerComponent): void => {
      const originalOnHover = bsDatepickerContainerComponent.dayHoverHandler;

      bsDatepickerContainerComponent.dayHoverHandler = function (event: CellHoverEvent): void {
        // Not apply hover for touch devices
        if (!detectMobile()) {
          originalOnHover.call(this, event);
          self.showDayDisabledTooltip(event);
        }
      };
    };

    const replaceNgOnInit = (): void => {
      const originalNgOnInit = (<any>this.bsDatepickerContainerComponent).__proto__.ngOnInit;
      (<any>this.bsDatepickerContainerComponent).__proto__.ngOnInit = function (): void {
        originalNgOnInit.call(this);
        this.daySelectHandler = replaceDaySelectHandler();
        replaceOnDayHoverMethod(this);
        self.internalDpMethods.monthSelectHandler = this.monthSelectHandler;
        self.internalDpMethods.yearSelectHandler = this.yearSelectHandler;
      };
    };

    const replaceNavToMethod = (): void => {
      const originalNavTo = (<any>this.bsDatepickerNavigationViewComponent).__proto__.navTo;
      (<any>this.bsDatepickerNavigationViewComponent).__proto__.navTo = function (down: boolean): void {
        const currentDate = self.tabValue.viewDate;
        const value = down ? -1 : 1;
        const newDate = moment(currentDate).add(value, 'month');
        self.tabValue.viewDate = newDate.toDate();
        originalNavTo.call(this, down);
        self.insertCustomDropdowns();
      };
    };
    replaceHideMethod();
    replaceViewMethod();
    replaceNavToMethod();
    replaceNgOnInit();
  }

  private showDayDisabledTooltip(event: { cell: DayViewModel, isHovered: boolean }): void {
    if (!event || !event.cell || !event.cell.isDisabled || !this.config || !this.config.dayTooltipTitles) {
      return;
    }

    const { cell : { date, weekIndex, dayIndex }, isHovered } = event;
    const { minDate, maxDate, dayTooltipTitles, disabledDates } = this.config;

    const isLessThanMin: boolean = minDate && dayTooltipTitles.disabledMinDate && moment(date) < moment(minDate);
    const isGreaterThanMax: boolean = maxDate && dayTooltipTitles.disabledMaxDate && moment(date) > moment(maxDate);
    const isDisabledDate: boolean = disabledDates && dayTooltipTitles.disabledDates
        && !!disabledDates.filter(disableDate => moment(disableDate).isSame(moment(date),  DateGranularity.DAY)).length;

    const tooltipElement = this.dayTooltipContainer.nativeElement;

    if (isLessThanMin) {
      this.dayTooltipTitle = dayTooltipTitles.disabledMinDate;
    }

    if (isGreaterThanMax) {
      this.dayTooltipTitle = dayTooltipTitles.disabledMaxDate;
    }

    if (isDisabledDate) {
      this.dayTooltipTitle = dayTooltipTitles.disabledDates;
    }

    if (!this.dayTooltipTitle) {
      return;
    }

    const disabledDaySelector =
      `.days.weeks tbody tr:nth-child(${weekIndex + 1}) td:nth-child(${dayIndex + 1}) span.disabled`;

    const targetElement: HTMLElement = this.elementRef.nativeElement
      .querySelector(disabledDaySelector);

    if (!targetElement) {
      return;
    }

    if (isHovered) {
      targetElement.appendChild(tooltipElement);
    } else {
      this.dayTooltipTitle = '';
      this.elementRef.nativeElement.append(tooltipElement);
    }
  }

  private setLocale(locale: string): void {
    const localeData: moment.Locale = moment.localeData();

    if (locale === 'en') {
      return;
    }

    const bsLocales = Object.values(locales);
    const particularLocale = bsLocales.find(b => b.abbr === locale);

    if (particularLocale) {
      defineLocale(particularLocale.abbr, particularLocale);
    }

    updateLocale(locale, this.normalizeDataForBootstrapLocale(localeData));
    this.bsLocaleService.use(locale);
  }

  private normalizeDataForBootstrapLocale(localeData: any): LocaleData {
    return {
      ...localeData._config,
    };
  }
}
